import { Injectable, Injector, inject } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localePt from '@angular/common/locales/pt';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { first } from 'rxjs';

import { TranslateService, TranslationObject } from '@ngx-translate/core';

import { App } from '@capacitor/app';

import { Platform } from '@ionic/angular';

import { environment } from '../../environments';
import { AuthService } from '../auth/auth.service';
import { CapacitiesService } from '../capacities/capacities.service';
import { PaletteThemeService } from '../palette-theme/palette-theme.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  private httpClient = inject(HttpClient);
  private injector = inject(Injector);
  private platform = inject(Platform);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private capacitiesService = inject(CapacitiesService);
  private paletteThemeService = inject(PaletteThemeService);
  private userService = inject(UserService);

  version = '';

  constructor() {
    this.registerTranslateModuleLanguages();
    this.preloadTranslations();
  }

  registerTranslateModuleLanguages(): void {
    registerLocaleData(localeEn, 'en');
    registerLocaleData(localeEs, 'es');
    registerLocaleData(localeFr, 'fr');
    registerLocaleData(localePt, 'pt-BR');
    registerLocaleData(localePt, 'pt-MZ');

    this.translateService.addLangs(environment.locales);
  }

  init(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.paletteThemeService.init();
      this.setAppVersion();
      this.setDefaultLanguage();

      /* Deactivated for now, as it causes issues with mobile web (tablets, etc.)
      if (!this.capacitiesService.isMobile && this.platform.is('mobileweb')) {
        this.router.navigate(['/redirect-mobile-app']);
        resolve();
        return;
      }
       */

      const authService: AuthService = this.injector.get(AuthService);
      authService.isLoggedIn().then((authenticated) => {
        if (authenticated) {
          this.userService
            .getCurrentUser()
            .pipe(first())
            .subscribe({
              next: (user) => {
                authService.setAuthenticatedUser(user);
                resolve();
              },
              error: () => {
                authService.disconnectLocally().finally(() => resolve());
              },
            });
        } else {
          resolve();
        }
      });
    });
  }

  setAppVersion(): void {
    App.getInfo()
      .then((info) => {
        this.version = info.version;
      })
      .catch(() => {
        // App not available, desktop browser
        this.version = environment.version;
      });
  }

  setDefaultLanguage(): void {
    const browserLang = this.translateService.getBrowserLang() ?? 'en';
    const lang = environment.locales.includes(browserLang) ? browserLang : 'en';
    this.translateService.setFallbackLang(lang);
    this.translateService.use(lang);
  }

  preloadTranslations(): void {
    environment.locales.forEach((locale) => {
      this.httpClient
        .get<TranslationObject>('/assets/i18n/' + locale + '.json')
        .subscribe((translations) => {
          this.translateService.setTranslation(locale, translations);
        });
    });
  }
}
